// التبويب الفرعي يبقى كما هو بعد التحديث (قرار المالك: «لما أبقى واقف في مكان أو
// تبويب أو عنصر وأعمل refresh، مش عاوز يغيّر المسار اللي أنا واقف فيه إطلاقًا»).
//
// التبويب **الرئيسي** محفوظٌ أصلًا في عنوان الصفحة (`#tab` في `App.tsx`)، فيعود بعد
// التحديث وحده. أمّا التبويب **الفرعي** داخل الصفحة («لوحة اليوم» · «الجداول» · …)
// فكان حالةً في الذاكرة تُمحى مع كل تحديث، فيرتدّ المستخدم إلى أوّل قسمٍ دائمًا —
// وهو أكثر ما يُفقد مكانه: من كان يقرأ «الجداول» ثم حدّث الصفحة وجد نفسه في «لوحة
// اليوم» بلا سبب يفهمه.
//
// **والتخزين في `sessionStorage` لا `localStorage`**: المكان يخصّ **هذه الجلسة**
// لا هذا الجهاز إلى الأبد. فمن أغلق المتصفّح وعاد غدًا يبدأ من أوّل قسمٍ كما يتوقّع،
// ومن حدّث الصفحة أو رجع إليها في نفس الجلسة يجد مكانه. وهو كذلك لا يُشارَك بين
// تبويبات المتصفّح، فنافذتان مفتوحتان على قسمين مختلفين تبقى كلٌّ في قسمها.
//
// **ولا يُصدَّق ما فيه على عِلّاته**: القيمة المحفوظة تُقارن بالأقسام المسموح بها،
// فقسمٌ حُذف من الشيفرة أو قيمةٌ عُبث بها لا تُعرض شاشةً فارغة — يُرجَع إلى الافتراضي.
// والقراءة والكتابة في `try` لأن التخزين يرمي في التصفّح الخاص وحين تُمنع المواقع.
import { useCallback, useEffect, useState } from 'react';

const key = (page: string) => `icontravel.section.${page}`;
const JUMP_EVENT = 'icontravel:jump-section';

/**
 * **القفز إلى قسمٍ من البحث** (قرار المالك: «أبحث عن كشف المرتبات… يدخلني عليها على طول»):
 * يُكتب القسم في المخزن الذي تقرؤه الصفحة عند تركيبها — فتفتح عليه إن لم تكن مفتوحة —
 * ويُبثّ حدثٌ تلتقطه إن كانت مفتوحةً فعلًا فتنتقل فورًا.
 */
export function jumpToSection(page: string, section: string): void {
  try { window.sessionStorage.setItem(key(page), section); } catch { /* تصفّح خاص أو تخزين ممنوع */ }
  try { window.dispatchEvent(new CustomEvent(JUMP_EVENT, { detail: { page, section } })); } catch { /* بيئة بلا CustomEvent */ }
}

/**
 * مثل `useState` تمامًا، لكنّه يتذكّر القسم المختار خلال الجلسة.
 *
 * @param page   اسمٌ ثابت للصفحة — به تُميَّز أقسامها عن أقسام غيرها.
 * @param allowed الأقسام المسموح بها؛ ما ليس فيها يُتجاهَل ويُرجَع للافتراضي.
 */
export function useStickySection<T extends string>(
  page: string, allowed: readonly T[], fallback: T,
): [T, (next: T) => void] {
  const [section, setSection] = useState<T>(() => {
    try {
      const saved = window.sessionStorage.getItem(key(page));
      return saved && (allowed as readonly string[]).includes(saved) ? saved as T : fallback;
    } catch { return fallback; }
  });

  const pick = useCallback((next: T) => {
    setSection(next);
    try { window.sessionStorage.setItem(key(page), next); } catch { /* تصفّح خاص أو تخزين ممنوع */ }
  }, [page]);

  // قفزةٌ من البحث والصفحة مفتوحة: القسم يتغيّر في مكانه
  useEffect(() => {
    const onJump = (e: Event) => {
      const d = (e as CustomEvent<{ page: string; section: string }>).detail;
      if (d?.page === page && (allowed as readonly string[]).includes(d.section)) setSection(d.section as T);
    };
    window.addEventListener(JUMP_EVENT, onJump);
    return () => window.removeEventListener(JUMP_EVENT, onJump);
  }, [page, allowed]);

  return [section, pick];
}

/**
 * **النزول إلى بطاقةٍ بعينها** (قرار المالك 01/10/2026: «لو كتبت تارجت المتابعات يظهر ليا أدخل فين على طول»): بعد
 * فتح التبويب يُنتظر ظهور العنصر (الصفحة تُحمَّل كسولًا) حتى أربع ثوانٍ، ثم يُنزل إليه ويُبرَز لحظةً ليُعرف.
 */
export function scrollToAnchor(id: string): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const started = Date.now();
  const tick = () => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
      const target = (el.firstElementChild as HTMLElement | null) ?? el;
      target.classList.add('ring-4', 'ring-amber-400', 'rounded-xl');
      window.setTimeout(() => target.classList.remove('ring-4', 'ring-amber-400', 'rounded-xl'), 1800);
      return;
    }
    if (Date.now() - started < 4000) window.setTimeout(tick, 150);
  };
  window.setTimeout(tick, 60);
}
